<template>
  <div>
    <input
      ref="fileInput"
      class="d-none"
      type="file"
      :accept="fileAccept"
      @change="fileChosen"/>

    <DataTable
    :items="files"
    :headers="headers"
    :actions="tableActions"
    :loading="loading"
    :customCells="customColumns"
    :showSearch="false"
    showPointerCursor
    containerClass="pa-0"
    htmlIdentifier="tract-visit-attachments"
    @gallery="openGallery"
    @upload="$refs.fileInput.click()"
    @click:row="openAttachment($event.data)">
      <template #search-replacement v-if="label && !$vuetify.breakpoint.xs">
        <span class="subtitle-1">{{ label }}</span>
      </template>
      <template #custom-controls v-if="label && $vuetify.breakpoint.xs">
        <v-col cols="auto">
          <span class="subtitle-1 mr-2">{{ label }}</span>
        </v-col>
      </template>
      <template #name="{item}">
        {{ item.name }}
        <v-chip v-if="item.pending" x-small outlined color="primary" class="ml-1">{{ $t('pending') }}</v-chip>
      </template>
      <template #type="{item}">
        {{ startCase(item.type) }}
      </template>
      <template #actions="{item}">
        <Icon
        v-if="item.pending"
        icon="mdi-close"
        iconColor="error"
        elementId="remove-attachment"
        :tooltipText="$t('remove')"
        @icon-clicked="$emit('remove', item)"/>
        <template v-else>
          <Icon
          icon="mdi-download"
          iconColor="success"
          elementId="download-attachment"
          :tooltipText="$t('downloadFile')"
          @icon-clicked="downloadFile(item)"/>
          <Icon
          v-if="allowDelete"
          icon="mdi-delete-forever"
          iconColor="error"
          elementId="delete-attachment"
          :tooltipText="$t('deleteFile')"
          @icon-clicked="$emit('delete', item)"/>
        </template>
      </template>
      <template #footer>
        <div class="text-right">{{ fileLimitFooter }}</div>
      </template>
    </DataTable>

    <Dialog
    :stateId="viewerDialogId"
    @dialog-closing="viewerPage = 0"
    @close="viewerPage = 0">
      <TractVisitEvidenceViewer
      v-if="viewerPage > 0"
      :tract="tract"
      :files="evidence"
      :propPage="viewerPage"/>
    </Dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { saveAs } from 'file-saver'
import { startCase } from 'lodash'
import TractVisitHeaders from '@/headers/TractVisit.js'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { validateVisitFile, VISIT_FILE_ACCEPT } from '@/utils/TractVisitFiles'
import { MAX_ATTACHMENT_FILES } from '@/utils/Files'
import { downscaleImage } from '@/utils/Images'
import { hasTouchScreen } from '@/utils/DeviceUtility'

export default {
  name: 'TractVisitAttachments',

  components: {
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    TractVisitEvidenceViewer: () => import('@/components/tract/tract-visits/TractVisitEvidenceViewer.vue')
  },

  props: {
    tract: {
      type: Object,
      required: true
    },
    files: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    },
    allowDelete: {
      type: Boolean,
      required: false,
      default: false
    },
    label: {
      type: String,
      required: false,
      default: ''
    }
  },

  data: () => ({
    fileAccept: hasTouchScreen() ? undefined : VISIT_FILE_ACCEPT,
    viewerDialogId: uniqueDialogId('tract-visit-attachment-viewer'),
    viewerPage: 0
  }),

  computed: {
    headers () {
      return TractVisitHeaders.tractVisitAttachmentHeaders()
    },

    customColumns () {
      return [
        { slotName: 'name', value: 'name' },
        { slotName: 'type', value: 'type' },
        { slotName: 'actions', value: 'actions' }
      ]
    },

    tableActions () {
      return [
        {
          actionName: 'gallery',
          icon: 'mdi-image-multiple-outline',
          text: this.$t('gallery'),
          disabled: this.evidence.length === 0,
          id: 'open-visit-gallery'
        },
        {
          actionName: 'upload',
          icon: 'mdi-plus',
          text: this.$t('uploadFile'),
          disabled: this.files.length >= MAX_ATTACHMENT_FILES,
          id: 'add-visit-attachment'
        }
      ]
    },

    fileLimitFooter () {
      return `${this.files.length} / ${MAX_ATTACHMENT_FILES} ${this.$t('files')}`
    },

    evidence () {
      return this.files.filter(f => !f.pending)
    }
  },

  methods: {
    ...mapActions('dialog', ['openOrUpdateDialog']),

    startCase,

    async fileChosen (e) {
      const picked = e.target.files[0]
      e.target.value = ''
      if (!picked || this.files.length >= MAX_ATTACHMENT_FILES) return

      const file = picked.type.startsWith('image/') ? await downscaleImage(picked) : picked
      const error = validateVisitFile(file)
      if (error) {
        this.setSnackError(error)
        return
      }

      this.$emit('file-chosen', file)
    },

    openAttachment (file) {
      if (file.pending) return
      this.openViewer(this.evidence.indexOf(file) + 1)
    },

    openGallery () {
      if (this.evidence.length > 0) this.openViewer(1)
    },

    openViewer (page) {
      this.viewerPage = page
      this.openOrUpdateDialog({ id: this.viewerDialogId, width: '90vw' })
    },

    async downloadFile (file) {
      const fetchedFile = await fetch(file.ref)
      const blob = await fetchedFile.blob()
      saveAs(blob, file.name)
    }
  }
}
</script>
